import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'withRouter';;
import classNames from 'classnames';

import SortableRow from 'components/Table/SortableRow';
import { getDuration } from 'utils/common';
import AHref from 'components/AHref';
// import Menu from './SingleMenu';
import {
  dateFormatter,
  dateFormatter4,
} from 'utils/common';
import {formatDate, getRoundedDecimalData} from './utils';
import { CHART_DECIMAL } from '../chartConfigurationsV2'

const getMetricRowProperties = (metricConfig, minMetric, totalMetric, metricDataValue, decimal) => {
  let fontColor = ''
  let activeLabel = ''
  const background = {
    active: false,
    color: ''
  }
  const indicatorBar = {
    active: false,
    color: '#FF6F91',
    indicatorBarWidth: 0
  }

  if (!metricConfig || (typeof metricDataValue !== 'number' && typeof metricDataValue !== 'string')) {

    return {
      fontColor,
      background,
      indicatorBar
    }
  }
  const numericData = parseFloat(metricDataValue)
  const minVal = typeof minMetric === 'number' ? minMetric : 0
  const maxVal = typeof totalMetric === 'number' ? totalMetric : 0
  const denom = (maxVal - minVal) > 0 ? (maxVal - minVal) : 0
  const rawPercent = denom > 0 ? ((numericData - minVal) / denom) * 100 : 0
  const percentageValue = Math.max(0, Math.min(100, rawPercent))

  if (metricConfig?.colorTiers?.customConfigs?.length > 0) {
    const tiers = metricConfig.colorTiers.customConfigs;
    // Determine if tiers are meaningfully configured; otherwise skip applying defaults
    const hasMeaningfulTiers = (() => {
      const type = metricConfig?.allThresholdType || 'absolute'
      if (type === 'label') {
        // require at least one tier with both a non-empty value and non-empty text
        return tiers.some(t => {
          const val = (t?.value === null || t?.value === undefined) ? '' : String(t.value).trim()
          const txt = (t?.text || '').trim()
          return val.length > 0 && txt.length > 0
        })
      }
      // absolute/percent: require at least one tier with numeric value > 0
      return tiers.some(t => Number.isFinite(parseFloat(t?.value)) && parseFloat(t.value) > 0)
    })()

    if (!hasMeaningfulTiers) {
      // Do not apply any coloring or label overrides if tiers are not meaningfully configured
      return {
        fontColor,
        background,
        indicatorBar,
        activeLabel
      }
    }
    let lastActiveColor = ''
    let matched = false
    activeLabel = ''

    if (metricConfig?.allThresholdType === 'absolute') {
      for (const tier of tiers) {
        if (getRoundedDecimalData(numericData, decimal) >= parseFloat(tier.value)) {
          lastActiveColor = tier.color;
          activeLabel = tier.text
          matched = true
        }
      }
    }

    if (metricConfig.allThresholdType === 'percent') {
      for (const tier of tiers) {
        if (getRoundedDecimalData(percentageValue, decimal) >= parseFloat(tier.value)) {
          lastActiveColor = tier.color;
          activeLabel = tier.text
          matched = true
        }
      }
    }

    if (metricConfig?.allThresholdType === 'label') {
      const findTier = tiers.find(tier => {
        const normalize = (v) => (v === null || v === undefined) ? '' : String(v).trim()
        const tierStr = normalize(tier.value)
        return normalize(metricDataValue) === tierStr
      })

      if (findTier) {
        lastActiveColor = findTier.color;
        activeLabel = findTier?.text
        matched = true
      }
    }

    if (matched && metricConfig.coloring === 'background') {
      const splitColor = lastActiveColor.split('')
      splitColor.splice(7, 2)
      const removeOpacity = splitColor.join('')
      background.active = true
      background.color = `${removeOpacity}1A`
      fontColor = lastActiveColor
      activeLabel = activeLabel
    }

    if (matched && metricConfig.coloring === 'font') {
      fontColor = lastActiveColor
      activeLabel = activeLabel
    }

    if (matched && metricConfig.coloring === 'indicatorBar') {

      indicatorBar.active = true
      indicatorBar.color = lastActiveColor
      indicatorBar.indicatorBarWidth = percentageValue
      activeLabel = activeLabel
    }
  }

  return {
    activeLabel,
    fontColor,
    background,
    indicatorBar
  }
}

class TableChartRow extends PureComponent {
  getMetricValue = (row) => {
    // 1) If any tier matched and provided an override text, always display it
    //    (applies to absolute, percent, and label threshold types).
    if (row?.rowProps?.activeLabel && String(row.rowProps.activeLabel).trim().length > 0) {
      return row.rowProps.activeLabel
    }

    if(row?.selectedMetric?.length === 1 && row?.selectedMetric?.includes('time')) {
      return row.metricData
    }

    // 2) Fallback for 'label' (Match Value) in cases where rowProps didn't set activeLabel.
    //    Compute override text directly from tiers for editor preview consistency.
    if (row?.allThresholdType === 'label') {
      const tiers = row?.tiersConfig || []
      const normalize = (v) => (v === null || v === undefined) ? '' : String(v).trim()
      const dataStr = normalize(row?.metricData)
      const match = tiers.find(t => normalize(t?.value) === dataStr)
      if (match && typeof match.text === 'string' && match.text.trim().length > 0) {
        return match.text
      }
    }

    // Otherwise, display the original metric value; do not replace it with threshold label text.
    // If decimals are set to 'fromData' and the metric value is a string, return it
    // with prefix/suffix while preserving the original formatting from the data source.
    if (row?.decimal === 'fromData') {
      return `${row?.prefix} ${row.metricData} ${row?.suffix}`
    }

    // Normalize decimal that may arrive as a numeric-like string (e.g., "2")
    const decNum = Number(row?.decimal)
    const hasNumericDecimal = Number.isFinite(decNum)
    const numeric = typeof row.metricData === 'number'
      ? row.metricData
      : (typeof row.metricData === 'string' ? parseFloat(row.metricData) : NaN)

    if (Number.isFinite(numeric)) {
      const value = hasNumericDecimal
        ? getRoundedDecimalData(numeric, decNum)
        : String(numeric);

      return `${row?.prefix} ${value} ${row?.suffix}`
    }

    return row.metricData
  }

  getRowRender = () => {
    const { columns, group, values, rows, me, colorOptions, totalMetrics, chartConfig, chartValues } = this.props
    const rowData = []
    const rowComponent = {}

    if (chartConfig.groupBy && chartConfig.groupBy.metrics && Object.keys(chartConfig.groupBy.metrics).length > 0) {
      Object.keys(chartConfig.groupBy.metrics).forEach((metricKey) => {
        if (metricKey === 'time') {
          rowComponent['timeInterval'] = () => (
            <div className='data-table__td_dark'>{group[groupKey]}</div>
          );
        } else {
          rowComponent[metricKey] = () => (
            <div>{group[groupKey]}</div>
          );
        }
      });
    }

    if (this.props.bucketed_time) {
      rowComponent['bucketed_time'] = () => {

        if(chartConfig.tableColumnMetricData === 'raw'){
          return (
            <div className='data-table__td_dark'>{this.props.bucketed_time}</div>
          );
        }
        
        return (
          <div className='data-table__td_dark'>{formatDate(this.props.bucketed_time, this.props.chartConfig.chartDateFormat)}</div>
        );
      }
    }

    if (chartConfig?.dataStreams?.streams && chartConfig.dataStreams.streams.length > 0) {
      // Build ordered list of metric column keys based on columns prop
      // Exclude time columns AND any non-time group-by columns to avoid misalignment in raw mode.
      const groupKeys = Array.isArray(chartConfig?.groupBy?.metrics)
        ? chartConfig.groupBy.metrics.filter(k => k !== 'time')
        : []

      const metricColumnKeys = (this.props.columns || [])
        .filter(c => !['bucketed_time', 'time', 'timeInterval', ...groupKeys].includes(c.sortKey))
        .map(c => c.sortKey)

      chartConfig.dataStreams.streams.forEach((stream, idx) => {
        const key = metricColumnKeys[idx]
        if (!key) return

        const totalMetric = totalMetrics ? totalMetrics[key] : undefined

        let metricData = typeof this.props[key] === 'number'
          ? parseFloat(this.props[key] || 0)
          : this.props[key];

        // Determine decimal places considering multiple possible storage locations.
        // Preference order:
        // 1) stream.decimal
        // 2) stream.metric.metricConfig.decimal
        // 3) stream.metric.metricConfig.decimal.selectedOption.value
        // 4) chartConfig.decimal
        // 5) CHART_DECIMAL.DEFAULT ('fromData')
        const candidateDecimals = [
          stream?.decimal,
          stream?.metric?.metricConfig?.decimal,
          stream?.metric?.metricConfig?.decimal?.selectedOption?.value,
          chartConfig?.decimal
        ]
        let resolvedDecimal = CHART_DECIMAL.DEFAULT
        for (const cand of candidateDecimals) {
          const num = Number(cand)
          if (Number.isFinite(num)) { resolvedDecimal = num; break }
          if (cand === 'fromData') { resolvedDecimal = 'fromData'; break }
        }
        const decimal = resolvedDecimal

        const minMetric = this.props.minMetrics ? this.props.minMetrics[key] : 0

        // Table editor: apply thresholds strictly per-stream. Do NOT fall back to chart-level values.
        const effectiveAllThresholdType = stream?.allThresholdType || 'absolute'

        // Use stream color tiers only if present; otherwise, treat as no tiers
        const streamColorTiers = stream?.colorTiers
        const effectiveColorTiers = (streamColorTiers?.customConfigs?.length ? streamColorTiers : { customConfigs: [] })

        // Use stream coloring only; if not set, disable coloring
        const effectiveColoring = stream?.coloring || ''

        // Build a merged config so getMetricRowProperties can evaluate thresholds properly in editor preview
        const mergedMetricConfig = {
          ...stream,
          coloring: effectiveColoring,
          colorTiers: effectiveColorTiers,
          allThresholdType: effectiveAllThresholdType,
        }

        const rowProps = getMetricRowProperties(mergedMetricConfig, minMetric, totalMetric, metricData, decimal)

        rowData.push({
          metric: key,
          totalMetric,
          metricData,
          rowProps,
          prefix: (stream?.prefix ?? chartConfig?.prefix ?? ''),
          suffix: (stream?.suffix ?? chartConfig?.suffix ?? ''),
          decimal,
          selectedMetric: stream?.metric?.metrics,
          allThresholdType: effectiveAllThresholdType,
          tiersConfig: effectiveColorTiers?.customConfigs || []
        })
      })
    }

    rowData.forEach(row => {
      rowComponent[row.metric] = () => {
        return (
          <div
            className='data-table__tags'
            style={row.rowProps.background.active ? { backgroundColor: row.rowProps.background.color } : {}}
          >
            <div>
              <div
                className={classNames('data-table__tags_title', {
                  'data-table__tags_title__indicator': row.rowProps.indicatorBar.active,
                  'data-table__tags_title__label': !row.rowProps.indicatorBar.active,
                })}
                style={{ color: row.rowProps.fontColor }}
                title={this.getMetricValue(row)}
              >
                {this.getMetricValue(row)}
              </div>

              {row.rowProps.indicatorBar.active &&
                <div className="billing-limit-progress-container data-table__tags_progressBar">
                  <div
                    className="billing-limit-progress"
                    style={{
                      background: row.rowProps.indicatorBar.color,
                      width: `${row.rowProps.indicatorBar.indicatorBarWidth}%`,
                    }}
                  />
                </div>
              }
            </div>
          </div>
        )
      }
    })

    return {
      ...rowComponent
    }
  }

  render() {
    const { colorClassName } = this.props;

    return (
      <>
        <SortableRow
          {...this.props}
          hideSingleMenu
          colorClassName={colorClassName}
          columns={this.props.columns}
          customRender={this.getRowRender()}
        >
          {/* <Menu
            id={this.props.id}
            onDeleteVersion={this.props.onDeleteVersion}
            application={this.props.application}
            detailRole={this.props.detailRole}
            onSetNewVersion={this.props.onSetNewVersion}
          /> */}
        </SortableRow>
      </>
    );
  }
}

TableChartRow.propTypes = {
  inputType: PropTypes.string,
  columns: PropTypes.array,
};

export default withRouter(TableChartRow);
